// Черновата на НОВО изписване / бракуване (`components/drafts/` — localStorage
// + сървърът, тип „writeoff"): листът такъв, какъвто е — видът, причината,
// складът, датата и редовете (и редовете-рецепти, и разделените на продукти).
// Същият обект е и `payload` на сървърната чернова: полетата не се
// преименуват, новите са незадължителни (`writeoffInitialOfDraft` допълва
// липсващите от празния ред).
import dayjs from "dayjs";
import {
  isVersionedObject,
  localDraftStore,
  type DraftPayloadBase,
  type DraftSummaryFields,
} from "../../components/drafts/draftModel";
import {
  AUTO_SCRAP_REASON,
  emptyWriteoffLine,
  type WriteoffKind,
  type WriteoffLineDraft,
  type WriteoffWorksheetInitial,
} from "./writeoffWorksheetShared";

export type WriteoffDraft = DraftPayloadBase & {
  v: 1;
  date: string; // ISO — пази и часа
  warehouseId: number | null;
  kind: WriteoffKind;
  reason: string;
  comment: string;
  lines: WriteoffLineDraft[];
};

const parseWriteoffDraft = (raw: unknown): WriteoffDraft | null => {
  if (!isVersionedObject(raw, [1])) return null;
  const draft = raw as unknown as WriteoffDraft;
  return Array.isArray(draft.lines) ? draft : null;
};

export const WRITEOFF_DRAFT_STORE = localDraftStore("inventory_writeoff_draft", parseWriteoffDraft);

// Празният лист (само вид/причина от бързото начало) не е чернова
export const hasWriteoffDraftContent = (draft: WriteoffDraft) => draft.lines.length > 0;

// Черновата → началното състояние на листа
export const writeoffInitialOfDraft = (
  draft: WriteoffDraft,
  fallbackWarehouseId: number | null,
): WriteoffWorksheetInitial => {
  const date = dayjs(draft.date);
  const lines = draft.lines.map((line) => ({ ...emptyWriteoffLine(line.key, line.kind), ...line }));
  return {
    date: date.isValid() ? date : dayjs(),
    warehouseId: draft.warehouseId ?? fallbackWarehouseId,
    kind: draft.kind === "scrap" ? "scrap" : "writeoff",
    // запазената причина на автоматичния брак би залостила листа (както при бързото начало)
    reason: draft.reason === AUTO_SCRAP_REASON ? "" : (draft.reason ?? ""),
    comment: draft.comment ?? "",
    lines,
    nextKey: lines.reduce((max, line) => Math.max(max, line.key), 0) + 1,
  };
};

// Етикетът на черновата в списъка: складът и причината („Бар · Изтекъл срок")
export const writeoffDraftSummary = (sheet: {
  warehouseName: string | null;
  reason: string;
  kind: WriteoffKind;
  linesCount: number;
  total: number | null;
}): DraftSummaryFields => ({
  label: [sheet.warehouseName, sheet.reason.trim()].filter(Boolean).join(" · ") || null,
  documentType: sheet.kind,
  linesCount: sheet.linesCount,
  total: sheet.total,
});

// Картата на локалната чернова (сумата иска наличностите към датата — няма я)
export const localWriteoffDraftSummary = (
  draft: WriteoffDraft,
  warehouseNameOf: (warehouseId: number | null) => string | null,
): DraftSummaryFields | null =>
  hasWriteoffDraftContent(draft)
    ? writeoffDraftSummary({
        warehouseName: warehouseNameOf(draft.warehouseId),
        reason: draft.reason ?? "",
        kind: draft.kind,
        linesCount: draft.lines.length,
        total: null,
      })
    : null;
